import { ExpressCheckoutRenderer } from '../expressPayment/ExpressCheckoutRenderer/ExpressCheckoutRenderer';
import { PaymentMethods } from '../PaymentMethods/PaymentMethods';
import { PaymentMethodForm } from '../PaymentMethodForm/PaymentMethodForm';
import { Footer } from '../Footer/Footer';
import { BackToStoreLink } from '../BackToStoreLink/BackToStoreLink';
import { DesktopViewer } from '../Common/Viewer';
import { Error } from '../Common/Error';
import { useTemplateFeatures } from '@/hooks/useTemplateFeatures';
import { useContext, useState } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { cn } from '@/utils/cn';
import { PAYMENT_STYLES, TEMPLATE_CLASSES } from '@/constants/styles';
import { useIsLandscape } from '@/hooks/useIsLandscape';
import { usePaymentSectionVisibility } from '@/hooks/usePaymentSectionVisibility';
import { useIsMobile } from '@/hooks/useIsMobile';
import { Collapsible } from '../Common/Collapsible';
import { DrawerScreen } from '@/types/screens';
import { CHECKOUT_CONTENT_END_ATTRIBUTE } from '@/utils/checkoutDimensions';
import { useExpressComponents } from '@/hooks/useExpressComponents';
import { useTrackCheckoutPresented } from '@/hooks/useTrackCheckoutPresented';
import { PaymentMethodAvailabilityProvider } from '@/context/PaymentMethodAvailabilityContext';
import { isRedirectModeRequested } from '@/utils/checkoutMode';

export const Payment = () => {
  const {
    shouldShowExpressCheckout: shouldShowExpressCheckoutTemplate,
    shouldShowPaymentMethods: shouldShowPaymentMethodsTemplate,
    shouldShowDesktopViewer,
    shouldShowPaymentMethodForm,
  } = useTemplateFeatures();
  const { shouldShowExpressCheckoutByScreen, shouldShowPaymentMethodsByScreen } = usePaymentSectionVisibility();
  const context = useContext(CheckoutContext);
  const [blockedForZipCode, setBlockedForZipCode] = useState(false);
  // Owned here, not inside `ExpressCheckoutRenderer`: wallet readiness decides
  // both what the row renders and when CHECKOUT_PRESENTED can be trusted.
  const expressState = useExpressComponents();
  useTrackCheckoutPresented(expressState);
  // ACDEV-25067: `isZipCodeValid` drives the `ExpressCheckoutRenderer`'s ZIP-blocker
  // overlay (only rendered when `WALLET_ZIP_TAX` is OFF — the FF-ON path collects
  // ZIP inside the wallet sheet). Setter still fed from the ZIP form via
  // `PaymentMethodForm` regardless of FF so the state is ready for either mode.
  const [isZipCodeValid, setIsZipCodeValid] = useState(false);
  const isMobile = useIsMobile();
  const landscape = useIsLandscape();
  const {
    template,
    // Gates the overlay below rather than letting `<Error />` no-op inside it,
    // so an empty positioned box never sits over the form.
    error,
    payment: { isInPaymentProcess, isExpressPaymentInProcess },
    threeDSSecurityState: { show3DSForm },
    drawer: { screen: drawerScreen },
    viewer: { screen },
  } = context;
  const {
    compactDetailsLayout,
    mobileOnlyExpressFirstLayout,
    footerFollowsContent,
    errorFollowsContent,
    showBackToStoreLink,
  } = template.features;
  // `mobileOnlyExpressFirstLayout` is currently true only for the PREFERRED
  // template (FTD / last-payment). We extend the qualifier to include the
  // landscape phone band so the express-first stack (single auto-selected
  // wallet button, no method tabs, no internal Footer) also drives the
  // landscape branch of `PreferredPaymentView`. DEFAULT/SINGLE keep the
  // flag at false, so this is a no-op for them.
  const isExpressFirstMobile = mobileOnlyExpressFirstLayout && (isMobile || landscape);
  const isPaymentInFlight = isInPaymentProcess || isExpressPaymentInProcess;
  const shouldBlockPaymentInteractions = isPaymentInFlight && !show3DSForm;
  /**
   * Mounting the link pulls in `useCancelCheckout` — a cancel-order mutation
   * and a redirect-link builder. Both halves of the condition are resolved
   * here so templates and modes that never offer it don't mount those hooks
   * at all, rather than having the component render `null` after paying for
   * them.
   */
  const showBackToStore = showBackToStoreLink && isRedirectModeRequested();
  /**
   * On desktop the section grows with its content while the PARENT column owns
   * the single scroll viewport (see `paymentColumnDesktopGrow`). Express-first
   * (PREFERRED on mobile/landscape) is a short, centered CTA that must NOT grow
   * to fill / push a footer, so it opts out.
   */
  // When a desktop viewer screen is open (e.g. SavedCardList), the section must
  // stay a DEFINITE height (the base `h-full` column height) instead of growing
  // with content — that lets the viewer pin its footer/CTA and scroll only its
  // inner list. The growable model is for the normal (form) desktop column.
  const shouldGrowPaymentColumnOnDesktop = !isExpressFirstMobile && !landscape && screen !== DrawerScreen.SAVED_CARDS;
  /**
   * Landscape DEFAULT picker uses the SAME single-scroll-column model as
   * desktop so the Footer scrolls together with the content instead of being
   * pinned by an inner-stack scroll viewport. SINGLE (compact) owns its scroll
   * in OnePaymentView, and express-first PREFERRED is a short centered CTA, so
   * both opt out.
   */
  const shouldGrowPaymentColumnInLandscape = landscape && !isExpressFirstMobile && !compactDetailsLayout;

  const paymentSectionClass = cn(
    PAYMENT_STYLES.base,
    'relative',
    template.styles.paymentContainer,
    shouldGrowPaymentColumnOnDesktop && TEMPLATE_CLASSES.paymentColumnDesktopGrow,
    shouldGrowPaymentColumnInLandscape && TEMPLATE_CLASSES.paymentColumnLandscapeGrow,
    // Layered LAST so tailwind-merge can drop the `h-full` from
    // `PAYMENT_STYLES.base` and the `justify-between` from the template's
    // `paymentContainer` — both of which spread form and Footer apart.
    footerFollowsContent && TEMPLATE_CLASSES.paymentColumnContentHeight
  );
  const paymentInnerStackClass = cn(
    TEMPLATE_CLASSES.paymentInnerStack,
    footerFollowsContent && TEMPLATE_CLASSES.paymentInnerStackContentHeight,
    isExpressFirstMobile && TEMPLATE_CLASSES.paymentInnerStackExpressFirstMobile,
    !compactDetailsLayout && TEMPLATE_CLASSES.paymentInnerStackDesktopTopOffset,
    shouldBlockPaymentInteractions && TEMPLATE_CLASSES.paymentInnerStackDisabled,
    landscape && TEMPLATE_CLASSES.paymentInnerStackLandscape,
    // Portrait DEFAULT only: keep the legacy mobile content-fit (reset at md so
    // desktop uses the section-grow scroll, which Safari needs). Preserves the
    // exact mobile behavior from before the desktop scroll fix.
    !isExpressFirstMobile && !landscape && !compactDetailsLayout && TEMPLATE_CLASSES.paymentInnerStackMobileContentFit
  );

  return (
    <div
      data-testid="payment-section"
      // Only meaningful while the section hugs its content: this box then ends
      // where the Footer below ends, which is the height the embedding SDK is
      // told to size its iframe to (see `measureCheckoutHeight`).
      {...{ [CHECKOUT_CONTENT_END_ATTRIBUTE]: footerFollowsContent || undefined }}
      className={paymentSectionClass}
    >
      <PaymentMethodAvailabilityProvider blockedForZipCode={blockedForZipCode}>
      <div className={paymentInnerStackClass}>
        {shouldShowExpressCheckoutTemplate && (
          <Collapsible visible={shouldShowExpressCheckoutByScreen}>
            <ExpressCheckoutRenderer
              onDisabledButtonClicked={() => {
                setBlockedForZipCode(true);
              }}
              blockedForZipCode={blockedForZipCode}
              isZipCodeValid={isZipCodeValid}
              expressState={expressState}
            />
          </Collapsible>
        )}
        {shouldShowPaymentMethodsTemplate && !blockedForZipCode && (
          <Collapsible visible={shouldShowPaymentMethodsByScreen}>
            <PaymentMethods />
          </Collapsible>
        )}
        {shouldShowPaymentMethodForm && (
          <PaymentMethodForm
            blockedForZipCode={blockedForZipCode}
            setBlockedForZipCode={setBlockedForZipCode}
            setIsZipCodeValid={setIsZipCodeValid}
          />
        )}
        {shouldShowDesktopViewer && <DesktopViewer />}
        {/* While a mobile drawer screen is open the drawer renders its own
            <Error /> on top of the overlay; suppress this one to avoid a
            duplicate hidden behind it. `drawer.screen` is only ever set on
            mobile (desktop uses the viewer), so desktop is unaffected.
            ACDEV-27000: on mobile this main-form error floats as a top-pinned,
            auto-dismissing overlay (no layout shift); desktop stays inline.
            `errorFollowsContent` templates float it above the Footer instead
            (below), so it isn't rendered twice. */}
        {!drawerScreen && !errorFollowsContent && <Error />}
        {showBackToStore && <BackToStoreLink />}
      </div>
      {!isExpressFirstMobile && (
        <div
          className={cn(
            TEMPLATE_CLASSES.footerWrapper,
            TEMPLATE_CLASSES.footerWrapperEdgeToEdgeMobile,
            footerFollowsContent && TEMPLATE_CLASSES.footerWrapperSticky,
            // Anchor for the error overlay below.
            errorFollowsContent && 'relative'
          )}
        >
          {/* Hosted by the Footer wrapper so the overlay can hang off its TOP
              edge — the banner then floats over the end of the form and nothing
              moves down when it appears. `errorFollowsContent` is only set on
              templates that render this Footer. */}
          {errorFollowsContent && !drawerScreen && !!error && (
            <div data-testid="payment-error-above-footer" className={TEMPLATE_CLASSES.errorOverlayAboveFooter}>
              <Error />
            </div>
          )}
          <Footer />
        </div>
      )}
      {shouldBlockPaymentInteractions && (
        <div
          data-testid="payment-in-progress-interaction-blocker"
          aria-hidden
          className="absolute inset-0 z-[2147483647] cursor-not-allowed pointer-events-auto"
          onPointerDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        />
      )}
      </PaymentMethodAvailabilityProvider>
    </div>
  );
};
